/**
 * SpeechRecognition wrapper + restart guard.
 *
 * Thin on purpose: everything that decides what a result *means* lives in
 * transcript.js, where it can be tested. This file owns the parts that only
 * exist because of how the browser API behaves.
 *
 * **The restart guard is the whole reason this file is not four lines.**
 * `continuous` does not mean continuous. Chrome ends the session on silence,
 * after a final result, and sometimes for no reason it shares. Restarting from
 * `onend` is therefore mandatory — but restarting immediately, or restarting
 * after the user asked to stop, produces a loop that grabs the microphone
 * several times a second and cannot be switched off without reloading the page.
 * So: an explicit intent flag, a delay before every restart, and a hard stop on
 * the errors that mean "do not ask again".
 *
 * Note for the README, not for this file: this is the one part of the project
 * that leaves the device. Chrome streams the microphone to Google to do the
 * transcription, by a path the CSP cannot see or restrict.
 */

import { config } from '../core/config.js';

const SpeechRecognitionCtor =
  typeof window !== 'undefined'
    ? (window.SpeechRecognition ?? window.webkitSpeechRecognition)
    : undefined;

/**
 * Whether this browser has a model for a language, when it will say.
 *
 * The failure this exists for is measured, not hypothetical. On one Chrome
 * desktop, `pt-BR` produced no results, no errors and no end events across two
 * takes and 340 seconds of loud speech, while `en-US` transcribed instantly in
 * the same page load with the same microphone. Chrome accepted the language,
 * reported itself as listening, and returned nothing — which is the least
 * debuggable way an API can fail, and from the outside is indistinguishable from
 * a broken microphone or a broken app.
 *
 * Chrome grew an availability API for exactly this. It is recent and this is the
 * only place that touches it, so every part is feature-detected: a browser
 * without it returns `null`, meaning "no opinion", and nothing downstream may
 * treat that as a problem.
 *
 * @param {string} lang BCP-47
 * @returns {Promise<?string>} 'available' | 'downloadable' | 'downloading' |
 *   'unavailable', or null when the browser will not say
 */
export async function languageAvailability(lang) {
  const available = SpeechRecognitionCtor?.available;
  if (typeof available !== 'function') return null;
  try {
    const status = await available.call(SpeechRecognitionCtor, {
      langs: [lang],
      // The cloud path is what this project has always used, and what the README
      // warns about. Asking about on-device would answer a question nobody here
      // is asking and report `unavailable` on a browser that works fine.
      processLocally: false,
    });
    return typeof status === 'string' ? status : null;
  } catch {
    return null;
  }
}

/**
 * Ask the browser to fetch a language model it says it could.
 *
 * Only ever called for a `downloadable` language, and only on a gesture, because
 * it may be a large download. Returns whether it worked; false is not an error
 * worth interrupting anyone over, it just means the status stays what it was.
 */
export async function installLanguage(lang) {
  const install = SpeechRecognitionCtor?.install;
  if (typeof install !== 'function') return false;
  try {
    return Boolean(await install.call(SpeechRecognitionCtor, { langs: [lang] }));
  } catch {
    return false;
  }
}

/** Whether Channel A can run here at all. Firefox has no implementation. */
export function recognitionSupported() {
  return Boolean(SpeechRecognitionCtor);
}

/**
 * Errors that mean stop asking.
 *
 * `not-allowed` and `service-not-allowed` are refusals, not hiccups. Restarting
 * after one either spams a permission prompt or silently burns battery retrying
 * something that will never succeed.
 */
const FATAL_ERRORS = new Set([
  'not-allowed',
  'service-not-allowed',
  'bad-grammar',
  'language-not-supported',
]);

export class Recognition {
  /**
   * @param {object} o
   * @param {(result: {text: string, isFinal: boolean, now: number}) => void} o.onResult
   * @param {(state: string, detail?: string) => void} [o.onState]
   */
  constructor({ onResult, onState }) {
    this.onResult = onResult;
    this.onState = onState ?? (() => {});

    /** 'idle' | 'listening' | 'error' | 'unsupported' */
    this.state = recognitionSupported() ? 'idle' : 'unsupported';
    /** What the *user* asked for, as distinct from what the API is doing. */
    this._wantListening = false;
    this._rec = null;
    this._restartTimer = null;
    /** Consecutive restarts without a result, to notice a spin. */
    this.restarts = 0;
  }

  _set(state, detail) {
    this.state = state;
    this.onState(state, detail);
  }

  _build() {
    const rec = new SpeechRecognitionCtor();
    rec.lang = config.speech.lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      // Timestamp here, at delivery. transcript.js subtracts the ASR latency to
      // recover when the words were actually spoken.
      const now = performance.now();
      this.restarts = 0;

      // Results accumulate across a continuous session; only those from
      // resultIndex on are new. Finals are emitted before interims so the
      // transcript freezes the completed utterance before binding what follows.
      let finalText = '';
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0]?.transcript ?? '';
        if (result.isFinal) finalText += text;
        else interimText += text;
      }

      if (finalText.trim()) this.onResult({ text: finalText.trim(), isFinal: true, now });
      if (interimText.trim()) this.onResult({ text: interimText.trim(), isFinal: false, now });
    };

    rec.onerror = (event) => {
      const err = event.error;
      // 'no-speech' and 'aborted' are ordinary punctuation in a long session,
      // not failures worth showing anyone.
      if (err === 'no-speech' || err === 'aborted') return;

      if (FATAL_ERRORS.has(err)) {
        this._wantListening = false;
        this._set('error', err);
        return;
      }
      this._set('listening', `recovering from ${err}`);
    };

    rec.onend = () => {
      if (!this._wantListening) {
        this._set('idle');
        return;
      }
      // The delay is load-bearing. Without it, an end that fires immediately on
      // start — which happens when the microphone is busy — becomes a tight
      // loop that takes the mic and never lets go.
      this.restarts++;
      this._restartTimer = setTimeout(() => this._safeStart(), config.speech.RESTART_DELAY);
    };

    return rec;
  }

  /** Start, tolerating the API already being started. */
  _safeStart() {
    if (!this._wantListening || !this._rec) return;
    try {
      this._rec.start();
      this._set('listening');
    } catch (err) {
      // InvalidStateError just means it is already running, which is fine.
      if (err?.name !== 'InvalidStateError') {
        this._set('error', err?.message ?? String(err));
      }
    }
  }

  start() {
    if (!recognitionSupported()) {
      this._set('unsupported', 'SpeechRecognition is not available in this browser');
      return;
    }
    if (this._wantListening) return;
    this._wantListening = true;
    this.restarts = 0;
    this._rec = this._build();
    this._safeStart();
  }

  stop() {
    // Cleared first: this flag is what onend consults, so clearing it after
    // calling abort() would race the restart it is meant to prevent.
    this._wantListening = false;
    clearTimeout(this._restartTimer);
    this._restartTimer = null;

    try {
      this._rec?.abort();
    } catch {
      // Already stopped; nothing to do.
    }
    this._rec = null;
    if (this.state !== 'error') this._set('idle');
  }

  get listening() {
    return this._wantListening;
  }
}

export default Recognition;
